import { Checkbox, Collapse, Grid, IconButton, Typography } from "@mui/material";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper.ts";
import type { ChartOfAccountsContextItem } from "../contexts/ChartOfAccountsContext";
import { useChartOfAccountsContext } from "../contexts/ChartOfAccountsContext";

const CHECKBOX_WIDTH_PL = 3.5;

interface Props {
  bookTitle: string;
  bookGroupId: string;
  parentNode: ChartOfAccountsContextItem | null;
  pl: number;
  children: (bookGroupNode: ChartOfAccountsContextItem | null, childPl: number) => ReactNode;
}

export default function FilterFieldBookGroupItem({ bookTitle, bookGroupId, parentNode, pl, children }: Props) {
  const { root, appendChild, getNode, onSelect } = useChartOfAccountsContext();
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!parentNode) return;
    appendChild(bookGroupId, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, bookGroupId]);

  const currentNode = useMemo(() => {
    if (!parentNode) return undefined;
    return getNode(bookGroupId, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, bookGroupId, root]);

  return (
    <>
      <Grid
        container
        sx={(theme) => ({
          pl,
          justifyContent: "space-between",
          bgcolor: currentNode?.checked ? makeTintedBackgroundFromColor(theme, theme.palette.primary.main) : "inherit",
          width: "100%",
        })}
      >
        <Grid container sx={{ alignItems: "center", pr: 0.625, height: "28px", width: "auto" }}>
          {currentNode && (
            <Checkbox
              indeterminate={currentNode.indeterminate === true}
              checked={currentNode.checked === true}
              sx={{ width: "28px", height: "28px" }}
              onChange={(e) => onSelect(currentNode.identifier, e.target.checked)}
            />
          )}
          <IconButton sx={{ width: "28px", height: "28px" }} onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Grid>
        <Grid container sx={{ alignItems: "center", pl: 0.625, pr: 1, height: "28px", flex: 1 }}>
          <Typography variant="caption" noWrap>
            {bookTitle}
          </Typography>
        </Grid>
      </Grid>
      <Collapse orientation="vertical" in={expanded}>
        {children(currentNode ?? null, pl + CHECKBOX_WIDTH_PL)}
      </Collapse>
    </>
  );
}
